import React, {useEffect, useState} from "react";
// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import {Pagination} from "swiper/modules";

import {PeerActivityActionButton, PeerActivityType} from "../../../redux/Content/PeerActivity/types";

import {MainFileImageTypes, MainFileType, MainFileVideoTypes} from "../../../redux/Main/File/types";
import TagItem from "../../../components/TagItem";

import '../../../i18/config';
import {useTranslation} from "react-i18next";
import {PeerActivityModeCode} from "../../../redux/Content/PeerActivityMode/types";
import {connectorHandlerCode} from "../../../constants/ConnectorHandler";
import ChannelAvatar from "../PeerChannel/channelAvatar";
import {ContentPeerChannelType} from "../../../redux/Content/PeerChannel/types";
import {isHTML} from "../../../helpers/functions";
import {channelHandle, gridLayout, NetworkSkin, networkSkin, PreviewAction} from "./Preview/networkSkin";

// The post as its network will show it: the editor's live preview, and what an approver
// sees before deciding. Each network's layout is described in Preview/networkSkin.ts.

interface ItemProps {
    items: PeerActivityType[];
    isShowPreviewMobile: boolean;
}

interface PreviewProps {
    item: PeerActivityType;
    skin: NetworkSkin;
}

const extension = (file: MainFileType) => (file.fileName || '').split('.').pop()?.toLowerCase() || '';
const isImage = (file: MainFileType) => (file.contentType || '').startsWith('image/') || (Object.values(MainFileImageTypes) as string[]).includes(extension(file));
const isVideo = (file: MainFileType) => (file.contentType || '').startsWith('video/') || (Object.values(MainFileVideoTypes) as string[]).includes(extension(file));
const visibleMedia = (item: PeerActivityType) => (item.media || []).filter((file: MainFileType) => (file.enabled === undefined || file.enabled) && (isImage(file) || isVideo(file)));
const fullUrl = (file: MainFileType) => file.downLoadUrl || file.url;

/** When the post goes out, as a feed prints it ("12 Oct, 14:00"); empty for an unscheduled draft. */
const usePostTime = (item: PeerActivityType, timeOnly = false) => {
    const { i18n } = useTranslation();
    const iso = item.activeFrom || item.start;
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
        return '';
    }

    return new Intl.DateTimeFormat(i18n.language || undefined, timeOnly
        ? { hour: '2-digit', minute: '2-digit' }
        : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date);
};

const PreviewText = ({ item, skin }: PreviewProps) => {
    const { t } = useTranslation(['common']);
    const maxShowLessSize = skin.layout === 'bubble' ? 400 : 160;
    const maxShowLessLines = skin.layout === 'bubble' ? 12 : 3;
    const [showMore, setShowMore] = useState(false);
    const text = item?.text || '';
    const html = isHTML(text);
    const lines = text.split('\n');
    const isLong = text.length > maxShowLessSize || (!html && lines.length > maxShowLessLines);

    if (!text && !(item.tags && item.tags.length > 0)) {
        return null;
    }

    const shortText = html
        ? text.substring(0, maxShowLessSize) + (text.length > maxShowLessSize ? '…' : '')
        : lines.slice(0, maxShowLessLines).join('\n').substring(0, maxShowLessSize);

    return (
        <div className="socialPreviewText">
            {text.length > 0 &&
                <div className="text-body" style={{ whiteSpace: html ? undefined : 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {skin.captionWithName && item.channel && <span className="fw-semibold me-1">{channelHandle(item.channel.channelLink, item.channel.name).replace(/^@/, '') || item.channel.name}</span>}
                    {html
                        ? <span dangerouslySetInnerHTML={{ __html: showMore || !isLong ? text : shortText }} />
                        : <>{showMore || !isLong ? text : shortText + '…'}</>
                    }
                    {isLong &&
                        <a href="#" className={"ms-1 " + (skin.captionWithName ? 'text-muted' : 'fw-medium')} onClick={(e) => { e.preventDefault(); setShowMore(!showMore); }}>
                            {showMore ? t('common:showLess') : t('common:showMore')}
                        </a>
                    }
                </div>
            }
            {item.tags && item.tags.length > 0 &&
                <div className="mt-1">
                    {item.tags.split(/,/).map((tag: string, key: number) => <TagItem tag={tag} key={key} />)}
                </div>
            }
        </div>
    );
};

const MediaFile = ({ file, playable }: { file: MainFileType; playable: boolean }) => {
    if (isVideo(file)) {
        return playable
            ? <video controls preload="metadata" src={fullUrl(file)} className="socialPreviewMediaFile" />
            : <div className="position-relative h-100">
                <video muted preload="metadata" src={fullUrl(file)} className="socialPreviewMediaFile" />
                <span className="socialPreviewPlay"><i className="ri-play-fill" /></span>
            </div>;
    }

    return <img src={fullUrl(file)} alt="" className="socialPreviewMediaFile" loading="lazy" />;
};

/** VK, Facebook, X, LinkedIn: several images share one block instead of sliding by. */
const MediaGrid = ({ media, rounded }: { media: MainFileType[]; rounded?: boolean }) => {
    const { shown, className, more } = gridLayout(media.length);

    return (
        <div className={"socialPreviewGrid " + className + (rounded ? ' rounded-3 overflow-hidden border' : '')}>
            {media.slice(0, shown).map((file, key) => (
                <div key={(file.uuid || '') + key} className="socialPreviewGridCell">
                    <MediaFile file={file} playable={media.length === 1} />
                    {more > 0 && key === shown - 1 && <div className="socialPreviewMore">+{more}</div>}
                </div>
            ))}
        </div>
    );
};

/** Instagram, Pinterest, TikTok: one at a time, swiped by hand, with a "1/3" counter. */
const MediaCarousel = ({ media, aspect }: { media: MainFileType[]; aspect: string }) => {
    const [index, setIndex] = useState(0);

    return (
        <div className="position-relative socialPreviewCarousel" style={{ aspectRatio: aspect }}>
            <Swiper pagination={{ clickable: true }} modules={[Pagination]} slidesPerView={1} onSlideChange={(s) => setIndex(s.activeIndex)} className="h-100">
                {media.map((file, key) => (
                    <SwiperSlide key={(file.uuid || '') + key}>
                        <MediaFile file={file} playable />
                    </SwiperSlide>
                ))}
            </Swiper>
            {media.length > 1 && <span className="socialPreviewCounter">{index + 1}/{media.length}</span>}
        </div>
    );
};

const Actions = ({ skin }: { skin: NetworkSkin }) => {
    const { t } = useTranslation(['peerActivity']);
    const action = (a: PreviewAction, key: number) => (
        <span key={a.icon + key} className={"d-inline-flex align-items-center gap-1" + (skin.spreadActions ? ' flex-fill justify-content-center' : '')}>
            <i className={a.icon + ' fs-18'} />{a.label && <span className="fs-13">{t('peerActivity:' + a.label)}</span>}
        </span>
    );

    if (skin.actions.length === 0 && !skin.endActions) {
        return null;
    }

    return (
        <div className={"d-flex align-items-center text-muted socialPreviewActions" + (skin.spreadActions ? ' border-top' : ' gap-3')}>
            {skin.actions.map(action)}
            {(skin.endActions || skin.views) &&
                <span className="ms-auto d-inline-flex align-items-center gap-3">
                    {skin.views && <i className="ri-eye-line fs-16" />}
                    {(skin.endActions || []).map(action)}
                </span>
            }
        </div>
    );
};

const CallToAction = ({ item }: { item: PeerActivityType }) => {
    const { t } = useTranslation(['engagement']);
    const buttons = (item.cta || []).filter((button: PeerActivityActionButton) => button.enabled);
    if (buttons.length === 0) {
        return null;
    }

    return (
        <div className="d-grid gap-1 socialPreviewCta">
            {buttons.map((button: PeerActivityActionButton, key: number) => (
                <div key={'ctaPreview_' + item.channel?.uuid + '_' + key} className="btn btn-sm btn-light text-primary position-relative">
                    {item?.channel?.connector?.handler?.code === connectorHandlerCode.CONNECTOR_HANDLER_FACEBOOK
                        ? t('engagement:button' + button.name)
                        : (button.name && button.name.length > 0 ? button.name : ' ')}
                    <i className="ri-arrow-right-up-line fs-10 position-absolute top-0 end-0 m-1 opacity-50" />
                </div>
            ))}
        </div>
    );
};

/** Name, then "@handle · time" (X) or "time · globe" (Facebook, LinkedIn) under or beside it. */
const Byline = ({ item, skin, channel }: PreviewProps & { channel: ContentPeerChannelType }) => {
    const time = usePostTime(item);
    const handle = skin.handle ? channelHandle(channel.channelLink, channel.name) : '';
    const meta = (
        <span className="text-muted fs-12 text-truncate">
            {handle}{handle && time ? ' · ' : ''}{time}
            {skin.publicMark && <i className="ri-earth-line ms-1" />}
        </span>
    );

    return skin.sideAvatar
        ? <div className="d-flex align-items-center gap-1 text-truncate"><span className="fw-bold text-body text-truncate">{channel.name}</span>{meta}</div>
        : <div className="d-flex flex-column text-truncate lh-sm"><span className="fw-semibold text-body text-truncate">{channel.name}</span>{meta}</div>;
};

const PreviewCard = ({ item, skin }: PreviewProps) => {
    const media = visibleMedia(item);
    const channel = item.channel;
    const mediaBlock = media.length === 0 ? null : skin.media === 'carousel'
        ? <MediaCarousel media={media} aspect={item.channel?.connector?.handler?.code === connectorHandlerCode.CONNECTOR_HANDLER_INSTAGRAM ? '4 / 5' : '1 / 1'} />
        : <MediaGrid media={media} rounded={skin.sideAvatar} />;
    const title = item.title ? <div className="fw-semibold text-body socialPreviewTitle">{item.title}</div> : null;

    if (skin.sideAvatar && channel) {
        // X: avatar in its own column, everything else beside it.
        return (
            <div className="card w-100 mb-2 socialPreview">
                <div className="d-flex gap-2 p-3">
                    <div className="flex-shrink-0"><ChannelAvatar item={channel} /></div>
                    <div className="flex-grow-1 min-w-0">
                        <div className="d-flex align-items-start"><Byline item={item} skin={skin} channel={channel} /><i className="ri-more-fill text-muted ms-auto" /></div>
                        {title}
                        <PreviewText item={item} skin={skin} />
                        {mediaBlock && <div className="mt-2">{mediaBlock}</div>}
                        <CallToAction item={item} />
                        <div className="mt-2"><Actions skin={skin} /></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card w-100 mb-2 socialPreview">
            {channel &&
                <div className="d-flex align-items-center gap-2 px-3 pt-3 pb-2">
                    <ChannelAvatar item={channel} />
                    <Byline item={item} skin={skin} channel={channel} />
                    <i className="ri-more-fill text-muted ms-auto" />
                </div>
            }
            {skin.textOnTop && <div className="px-3 pb-2">{title}<PreviewText item={item} skin={skin} /></div>}
            {mediaBlock}
            <CallToAction item={item} />
            {skin.textOnTop
                ? <div className="px-3 py-2"><Actions skin={skin} /></div>
                : <div className="px-3 py-2"><Actions skin={skin} /><div className="mt-2">{title}<PreviewText item={item} skin={skin} /></div></div>
            }
        </div>
    );
};

/** Telegram: a channel message, media on top, views and time in the corner. */
const PreviewBubble = ({ item, skin }: PreviewProps) => {
    const media = visibleMedia(item);
    const time = usePostTime(item, true);

    return (
        <div className="socialPreviewChat w-100 mb-2 p-2 rounded-3">
            <div className="socialPreviewBubble shadow-sm">
                {item.channel && <div className="fw-semibold text-primary px-2 pt-2">{item.channel.name}</div>}
                {media.length > 0 && <div className="pt-2"><MediaGrid media={media} /></div>}
                <div className="px-2 pt-1">
                    {item.title && <div className="fw-semibold text-body">{item.title}</div>}
                    <PreviewText item={item} skin={skin} />
                </div>
                <div className="d-flex justify-content-end align-items-center gap-1 text-muted fs-11 px-2 pb-1">
                    <i className="ri-eye-line" />{time && <span className="ms-1">{time}</span>}
                </div>
                <CallToAction item={item} />
            </div>
        </div>
    );
};

/** YouTube: the video at 16:9, then the title in bold and the channel under it. */
const PreviewVideo = ({ item }: PreviewProps) => {
    const media = visibleMedia(item);
    const time = usePostTime(item);

    return (
        <div className="w-100 mb-2 socialPreview">
            <div className="ratio ratio-16x9 rounded-3 overflow-hidden bg-dark">
                {media[0] ? <MediaFile file={media[0]} playable /> : <div className="d-flex align-items-center justify-content-center text-white-50"><i className="ri-video-line fs-24" /></div>}
            </div>
            <div className="d-flex gap-2 pt-2">
                {item.channel && <div className="flex-shrink-0"><ChannelAvatar item={item.channel} /></div>}
                <div className="min-w-0 lh-sm">
                    <div className="fw-semibold text-body socialPreviewTitle">{item.title || item.text}</div>
                    <div className="text-muted fs-12 mt-1">{item.channel?.name}{time ? ' · ' + time : ''}</div>
                </div>
                <i className="ri-more-2-fill text-muted ms-auto" />
            </div>
        </div>
    );
};

/** Pinterest: a tall rounded pin with the title and the account under it. */
const PreviewPin = ({ item, skin }: PreviewProps) => {
    const media = visibleMedia(item);

    return (
        <div className="w-100 mb-2 socialPreview">
            {media.length > 0 && <div className="rounded-4 overflow-hidden"><MediaCarousel media={media} aspect="2 / 3" /></div>}
            <div className="pt-2">
                {item.title && <div className="fw-semibold text-body socialPreviewTitle">{item.title}</div>}
                <PreviewText item={item} skin={skin} />
                {item.channel &&
                    <div className="d-flex align-items-center gap-2 mt-2 socialPreviewSmallAvatar">
                        <ChannelAvatar item={item.channel} /><span className="fs-12 text-body">{item.channel.name}</span>
                    </div>
                }
                <CallToAction item={item} />
            </div>
        </div>
    );
};

/** Stories, Reels, Shorts, TikTok: a 9:16 frame, the caption over the media, the actions down the side. */
const PreviewReel = ({ item, skin }: PreviewProps) => {
    const media = visibleMedia(item);
    const isStory = item?.mode?.code === PeerActivityModeCode.STORY;
    const [index, setIndex] = useState(0);
    const time = usePostTime(item);

    return (
        <div className="socialPreviewReel position-relative rounded-4 overflow-hidden bg-dark mb-2">
            {media.length > 0
                ? <Swiper slidesPerView={1} onSlideChange={(s) => setIndex(s.activeIndex)} className="h-100">
                    {media.map((file, key) => <SwiperSlide key={(file.uuid || '') + key}><MediaFile file={file} playable /></SwiperSlide>)}
                </Swiper>
                : <div className="h-100 d-flex align-items-center justify-content-center text-white-50"><i className="ri-camera-off-line fs-24" /></div>
            }
            {isStory
                ? <div className="socialPreviewReelTop">
                    <div className="d-flex gap-1 mb-2">
                        {(media.length > 0 ? media : [null]).map((_, key) => <span key={key} className={"socialPreviewStoryBar" + (key <= index ? ' active' : '')} />)}
                    </div>
                    {item.channel &&
                        <div className="d-flex align-items-center gap-2 text-white socialPreviewSmallAvatar">
                            <ChannelAvatar item={item.channel} /><span className="fw-semibold fs-12">{item.channel.name}</span>{time && <span className="fs-11 opacity-75">{time}</span>}
                        </div>
                    }
                </div>
                : <>
                    <div className="socialPreviewReelSide">
                        {[...skin.actions, { icon: 'ri-more-fill' }].map((a, key) => <i key={a.icon + key} className={a.icon} />)}
                    </div>
                    <div className="socialPreviewReelBottom">
                        {item.channel &&
                            <div className="d-flex align-items-center gap-2 mb-1 socialPreviewSmallAvatar">
                                <ChannelAvatar item={item.channel} /><span className="fw-semibold fs-12">{item.channel.name}</span>
                            </div>
                        }
                        {item.title && <div className="fw-semibold fs-12">{item.title}</div>}
                        {item.text && <div className="fs-12 socialPreviewReelCaption">{isHTML(item.text) ? item.text.replace(/<[^>]*>/g, ' ') : item.text}</div>}
                    </div>
                </>
            }
        </div>
    );
};

const PreviewItem = ({ items, isShowPreviewMobile = true}: ItemProps) => {

    const { t } = useTranslation(['common']);
    const [item, setItem] = useState<PeerActivityType>(null);
    const [selectedChannel, setSelectedChannel] = useState<ContentPeerChannelType>(null);

    const getItem = (items: PeerActivityType[], channel?: ContentPeerChannelType) => {
        let result;
        if (channel) {
            result = items.find((item:PeerActivityType) => item.enabled && item.channel.uuid === channel.uuid);
        }
        return result || items.find((item:PeerActivityType) => item.enabled);
    };

    useEffect(() => {
        const enabledItems = items.filter((item:PeerActivityType)=> item.enabled);
        let newItem = getItem(enabledItems, selectedChannel);
        if (!newItem) {
            newItem = getItem(enabledItems);
            setSelectedChannel(newItem?.channel);
        }

        setItem(newItem || null);

    },[items, selectedChannel]);

    const skin = networkSkin(item?.channel?.connector?.handler?.code);
    const isReel = item?.mode?.code === PeerActivityModeCode.SHORTS || item?.mode?.code === PeerActivityModeCode.STORY;

    return (
       <div className={"d-flex flex-column flex-grow-1 bg-light peerActivityPreviewCont"}>
           <div className="d-flex flex-column flex-grow-1 peerActivityPreviewWrapper p-3">
               <div>
                   <h6 className="text-center">{t('peerActivity:preview')}</h6>
               <div className="d-flex flex-row justify-content-center">
                   {(items && ((items || []).length>0)) &&
                       <div className={"d-flex flex-row pb-2"}>
                       { // eslint-disable-next-line
                           items.map((_item:PeerActivityType)=>{
                           if (_item.enabled && _item?.channel){
                               return(
                                    <a href={"#"} key={'previewTab_'+_item.channel.uuid} className={"p-2"+(_item.channel.uuid===selectedChannel?.uuid?'':' disabledBlock')} onClick={(e)=>{e.preventDefault(); setSelectedChannel(_item.channel)}}>
                                        <ChannelAvatar item={_item.channel}/>
                                    </a>
                               )
                           }
                       })}
                       </div>
                   }
               </div>
               <div className="d-flex flex-column justify-content-center align-items-center">
                    <div className={(isShowPreviewMobile || isReel ? ' previewMobile' : ' previewDesktop')}>
                    {(item !== undefined && item !== null) ?
                        <>
                            {isReel && <PreviewReel item={item} skin={skin} />}
                            {!isReel && item?.mode?.code === PeerActivityModeCode.ACTIVITY && <>
                                {skin.layout === 'card' && <PreviewCard item={item} skin={skin} />}
                                {skin.layout === 'bubble' && <PreviewBubble item={item} skin={skin} />}
                                {skin.layout === 'video' && <PreviewVideo item={item} skin={skin} />}
                                {skin.layout === 'pin' && <PreviewPin item={item} skin={skin} />}
                            </>}
                        </>
                        :
                        <div className="d-flex justify-content-center">
                            <i className="ri-camera-off-line fs-24 text-muted"/>
                        </div>
                    }
                    </div>
                </div>
           </div>
           </div>
       </div>
    );
};

export default PreviewItem;
